import AlarmResource from '@/api/resources/alarm'
import type { ISensorChartOverlayAlarm, SensorChartAlarmOrigin } from '@/interfaces/components/sensorChartAlarm'
import { GROUP_ONLY_ALARM_TYPES } from '@/interfaces/enums'

function normalizeOrigin(origin: string | undefined): SensorChartAlarmOrigin {
  return origin === 'group' ? 'group' : 'sensor'
}

function normalizeAlarm(raw: Record<string, unknown>): ISensorChartOverlayAlarm | null {
  const id = raw.id
  const startDate = raw.start_date
  if (typeof id !== 'number' || typeof startDate !== 'string') return null

  const alarmType = typeof raw.alarm_type === 'string' ? raw.alarm_type : ''
  const classification = raw.classification_type
  const sensor = raw.sensor
  const group = raw.group

  return {
    id,
    name: typeof raw.name === 'string' ? raw.name : undefined,
    alarm_type: alarmType,
    origin: normalizeOrigin(typeof raw.origin === 'string' ? raw.origin : undefined),
    start_date: startDate,
    end_date: typeof raw.end_date === 'string' ? raw.end_date : raw.end_date === null ? null : null,
    classification_type:
      classification && typeof classification === 'object' && classification !== null
        ? {
            id: Number((classification as { id?: unknown }).id),
            name: String((classification as { name?: unknown }).name ?? ''),
          }
        : null,
    sensor:
      sensor && typeof sensor === 'object' && sensor !== null
        ? {
            id: Number((sensor as { id?: unknown }).id),
            name: String((sensor as { name?: unknown }).name ?? ''),
          }
        : null,
    group:
      group && typeof group === 'object' && group !== null
        ? {
            id: Number((group as { id?: unknown }).id),
            name: String((group as { name?: unknown }).name ?? ''),
          }
        : null,
  }
}

function extractAlarmList(response: unknown): ISensorChartOverlayAlarm[] {
  if (!response || typeof response !== 'object') return []
  const data = (response as { data?: unknown }).data
  if (!Array.isArray(data)) return []

  return data
    .map((item) => (item && typeof item === 'object' ? normalizeAlarm(item as Record<string, unknown>) : null))
    .filter((item): item is ISensorChartOverlayAlarm => item != null)
}

export function mergeOverlayAlarms(lists: ISensorChartOverlayAlarm[][]): ISensorChartOverlayAlarm[] {
  const byId = new Map<number, ISensorChartOverlayAlarm>()

  for (const list of lists) {
    for (const alarm of list) {
      byId.set(alarm.id, alarm)
    }
  }

  return [...byId.values()].sort(
    (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
  )
}

export function filterChartOverlayAlarms(alarms: ISensorChartOverlayAlarm[]): ISensorChartOverlayAlarm[] {
  return alarms.filter((alarm) => alarm.origin === 'sensor')
}

export function alarmsToPeriodsMs(
  alarms: ISensorChartOverlayAlarm[],
  rangeEndMs: number,
): [number, number][] {
  return alarms.map((alarm) => {
    const start = new Date(alarm.start_date).getTime()
    const end = alarm.end_date ? new Date(alarm.end_date).getTime() : rangeEndMs
    return [start, end] as [number, number]
  })
}

export function resolveChartHoverIndex(
  alarm: ISensorChartOverlayAlarm | null,
  sensorAlarms: ISensorChartOverlayAlarm[],
): number | null {
  if (!alarm || alarm.origin === 'group') return null
  const index = sensorAlarms.findIndex((item) => item.id === alarm.id)
  return index >= 0 ? index : null
}

async function fetchAlarmsSafe(params: Parameters<typeof AlarmResource.getAlarms>[0]): Promise<ISensorChartOverlayAlarm[]> {
  try {
    const response = await AlarmResource.getAlarms(params)
    return extractAlarmList(response)
  } catch {
    return []
  }
}

export interface IFetchSensorChartOverlayAlarmsParams {
  sensorId: number
  sensorAlarmType: string
  startDate: string
  endDate: string
}

export async function fetchSensorChartOverlayAlarms({
  sensorId,
  sensorAlarmType,
  startDate,
  endDate,
}: IFetchSensorChartOverlayAlarmsParams): Promise<ISensorChartOverlayAlarm[]> {
  const baseParams = {
    start_date: startDate,
    end_date: endDate,
    sensor_id: sensorId,
    items: 200,
  }

  const groupTypes = [...GROUP_ONLY_ALARM_TYPES]

  const [sensorAlarms, ...groupAlarmLists] = await Promise.all([
    fetchAlarmsSafe({ ...baseParams, alarm_type: sensorAlarmType }),
    ...groupTypes.map((alarmType) =>
      fetchAlarmsSafe({
        ...baseParams,
        alarm_type: alarmType,
        include_group_alarms: true,
      }),
    ),
  ])

  return mergeOverlayAlarms([sensorAlarms, ...groupAlarmLists])
}
